/* eslint-disable no-underscore-dangle */
import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  getEntityType,
} from 'web-utils';

import {
  Spinner,
  NotFound,
  NestedWorkout,
  WorkoutHeader,
  WorkoutExercisesList,
  WorkoutExercisesFooter,
  WorkoutExercisesSubHeader,
} from 'web-components';

import {
  Flex,
  WorkoutExercisesView,
} from 'web-styled';

type Props = {
  id: number,
  readOnly: boolean,
  parentId: number,
  parentType: string,
  resourceType: string,
};

const AdditionalWorkout = ({
  id,
  readOnly,
  parentId,
  parentType,
  resourceType,
}: Props) => {
  const mapState = useCallback(state => ({
    status: getStatus(
      state,
      [
        `${resourceType}.requests.getFullAdditionalWorkout.status`,
        `${resourceType}.meta[${id}].readStatus`,
      ],
      true,
    ),
    options: coreSelectors.getEntitySearchOptions(
      resourceType,
      'library',
    )(state),
  }), [id]);

  const { status, options } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();
  useEffect(() => {
    if (id) {
      dispatch(
        coreActions.requestFullWorkout({
          id,
          resourceType: parentType,
          addtionalType: resourceType,
        }),
      );
    }
  }, [id]);

  const type = getEntityType(resourceType);

  const handleAdd = (payload, callbacks = {}) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: parentId,
        payload,
        resourceType: parentType,
        callbacks,
      }),
    );
  };

  return id ? (
    <Flex fillSpace>
      {status.succeeded && (
        <WorkoutExercisesView>
          <WorkoutHeader
            readOnly={readOnly}
            parentId={parentId}
            workoutId={id}
            parentType={parentType}
            resourceType={resourceType}
          />

          {!readOnly && (
            <WorkoutExercisesSubHeader
              workoutId={id}
            />
          )}

          <WorkoutExercisesList
            type={type}
            readOnly={readOnly}
            workoutId={id}
            workoutType={parentType === 'personalWorkouts'
              ? 'PersonalWorkout'
              : 'WorkoutTemplate'}
          />

          {!readOnly && (
            <WorkoutExercisesFooter
              workoutId={id}
              workoutType={parentType === 'personalWorkouts'
                ? 'PersonalWorkout'
                : 'WorkoutTemplate'}
            />
          )}
        </WorkoutExercisesView>
      )}
      {status.pending && (
        <Spinner message="Workout..." />
      )}
      {status.failed && (
        <NotFound
          title="Not Found"
        />
      )}
    </Flex>
  ) : (
    <NestedWorkout
      type={type}
      options={options}
      readOnly={readOnly}
      onRequestAdd={({ value }) => {
        handleAdd(type === 'warmup'
          ? { warmup_id: +value }
          : { cooldown_id: +value });
      }}
      onRequestAddNew={({ value }) => {
        const payload = type === 'warmup'
          ? { warmup_id: '0' }
          : { cooldown_id: '0' };

        const callbacks = {
          onSuccess: (result) => {
            dispatch(
              coreActions.requestUpdateEntity({
                id: type === 'warmup' ? result.warmup_id : result.cooldown_id,
                payload: { name: value },
                resourceType,
              }),
            );
          },
        };

        handleAdd(payload, callbacks);
      }}
    />
  );
};

export default AdditionalWorkout;
